import { memo, useState } from 'react'
import { showsNumbers } from '../../lib/dayCardDisplay'
import { useTexte } from '../../texte'
import { useEditMessage, useToggleReaction } from '../../hooks/queries'
import type { Media, Message } from '../../api/types'
import { Ausgezeichnet } from '../../components/Ausgezeichnet'
import { Highlightkarten } from './Highlightkarten'
import { Abstimmungskarte } from './Abstimmungskarte'
import { Wetterkarte } from './Wetterkarte'
import { Tageskarte } from './Tageskarte'
import { Musikkarte } from './Musikkarte'
import { Tagestrenner } from './Tagestrenner'
import { absenderZeit, bubbleTimeUnder, dayMoment, medienwort } from '../../lib/format'
import {
  CheckIcon,
  MoveIcon,
  DoubleCheckIcon,
  GruppeIcon,
  KopierenIcon,
  PencilIcon,
  PersonIcon,
  KalenderIcon,
  PinIcon,
  PlusIcon,
  ReplyIcon,
  SendIcon,
  SichernIcon,
  SmileyIcon,
  TrashIcon,
  SonneIcon,
  PollIcon,
  BergIcon,
  GifIcon,
} from '../../components/Icons'
import { Langtext } from '../../components/Langtext'
import { Lightbox } from './Gallery'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { useUhrwechsel } from '../../hooks/useUhrwechsel'
import { Blasenmenue, useLangdruck, type Menueeintrag, type Menueort } from './Blasenmenue'
import { Aenderungsblatt } from './Aenderungsblatt'
import { useOnline } from '../../hooks/useOutbox'
import { Gallery } from './Gallery'
import { EMOJI, Reactions } from './Reactions'
import { Reaktionsliste, zeigtFremdeNamen } from './Reaktionsliste'
import { Tonspur } from '../../components/Tonspur'
import { fehlertext } from '../../api/client'
import { notiere } from '../../lib/sendeprotokoll'
import type { Uhr } from '../../lib/ortszeit'
import { nurTrenner } from '../../lib/nachricht'

interface Props {
  message: Message
  tripId: number
  withUser?: number
  /** Namen über die Sprechblase setzen (im Antwortverlauf des Absenders). */
  showAuthor: boolean
  /** In einer abgeschlossenen Reise wird nichts mehr geschrieben. */
  canReact: boolean
  /**
   * Der Zonenverlauf der Reise (1.87.0) — für Karten ohne eigene Uhr.
   *
   * Sie gilt für jeden Punkt ohne eigene; ohne sie stünde auf dem Kärtchen die
   * Zeit des Geräts, und der Bericht läse sich zu Hause um Stunden verschoben.
   */
  reiseVersatz?: Uhr
  /** Beschriftung für eine private Nachricht -- je nachdem, wer sie liest. */
  directLabel?: string
  /**
   * Trägt auch ein Beitrag an alle seine Marke?
   *
   * **Eine Einstellung der Reise** (`trips.mark_broadcast`), keine des
   * Kontos: es ist eine Aussage über diesen Bericht. Die private Nachricht
   * trägt ihre unabhängig davon — sie ist die Ausnahme, und die gehört
   * benannt.
   */
  markiereBroadcast?: boolean
  /**
   * „Darauf antworten" -- ohne diese Funktion gibt es den Knopf nicht.
   *
   * `mediaId` kommt aus der Großansicht eines Fotos: dann bezieht sich die
   * Antwort auf genau dieses Bild und nicht auf den ganzen Beitrag.
   */
  onReply?: (message: Message, mediaId?: number) => void
  /** Zum bezogenen Beitrag springen -- der Tipp aufs Zitat. */
  onJump?: (messageId: number) => void
  /**
   * „Auf die Karte" — aus diesem Beitrag ein Highlight machen.
   *
   * Nur der Autor bekommt das, und nur an eigenen Beiträgen an alle. Ohne diese
   * Funktion gibt es den Knopf nicht.
   */
  onAufDieKarte?: (message: Message) => void
  /**
   * Der Auswahlmodus zum Loeschen (#30) -- oder nichts.
   *
   * **Der Verlauf haelt ihn, nicht die Blase.** Eine Auswahl ueber mehrere
   * Nachrichten kann keine einzelne kennen; hier kommt nur an, was diese eine
   * angeht.
   *
   * **Und er kommt nur an eigenen, nicht geloeschten Nachrichten an.** Der
   * Kreis ist eine Bequemlichkeit; die Sperre steht im Server
   * (`author_id = :user`) und bleibt dort. Zwei Stellen fuer dieselbe Regel
   * sind in diesem Projekt schon mehrfach auseinandergelaufen -- deshalb ist
   * die hier ausdruecklich keine.
   */
  auswahl?: { gewaehlt: boolean; waehle: (messageId: number) => void }
  /** „Löschen" im Menü: den Auswahlmodus öffnen, diese Nachricht schon darin. */
  onLoeschmodus?: (messageId: number) => void
  /**
   * "Verschieben" in the menu (#130): the selection for moving, this post
   * already in it. Only where posts can be moved -- the author's own posts
   * to everyone in the trip's chat.
   */
  onVerschiebemodus?: (messageId: number) => void
  /**
   * The author's private message to everyone (1.157.0). Only in a private
   * chat of the author; the server checks the kind again.
   */
  onPublishToAll?: (message: Message) => void
  /** Save the photos and videos of this post (1.159.0); from the menu, for everyone. */
  onSaveMedia?: (media: Media[]) => void
  /** The menu opened: a new choice, so an old error line goes (1.117.4). */
  onMenueAuf?: () => void
  /**
   * While posts wait for their target: a tap on this row chooses it. The
   * content is still under it, as in the selection, so that a photo does not
   * open instead.
   */
  ziel?: (message: Message) => void
  /** One of the rows on their way: marked like a picked row. */
  wirdVerschoben?: boolean
  /**
   * The draft chat (#85): this bubble is a draft, and publishing is in its
   * menu. Without a function the entry is not there, like everywhere in this
   * file.
   */
  entwurf?: boolean
  onVeroeffentlichen?: (message: Message) => void
  /**
   * Before or after this post (#130): the author's posts to everyone, in
   * the trip chat and in the draft. The page holds the marker above the
   * line and decides whether there is a net.
   */
  onEinfuegen?: (message: Message, where: 'before' | 'after') => void
  /**
   * A day separator changed (#113): its sheet, prefilled. A separator is a
   * post of its own since 1.115.0 (#130), deleted like any other.
   */
  onTagestrenner?: (message: Message) => void
  /**
   * A weather card about a chosen place and hour, changed (1.86.0): the
   * page opens the sheet prefilled. Not for the card from the device -- its
   * moment is the tap.
   */
  onWetter?: (message: Message) => void
  /** A poll, its question and words anew (1.86.0). */
  onAbstimmung?: (message: Message) => void
  /** A day card, computed anew (1.86.0). */
  onTag?: (message: Message) => void
  /** A post of nothing but a GIF: another GIF (1.86.0). */
  onGif?: (message: Message) => void
}

/**
 * A door for the test bench (1.155.0, review D4): `blasentakt.mjs` sets
 * `window.__tbBlasen` to a number and reads how many bubbles a tick drew.
 * One property read per render, nothing else; without the number it does
 * nothing.
 */
function zaehleZeichnung(): void {
  const tuer = globalThis as { __tbBlasen?: number }
  if (typeof tuer.__tbBlasen === 'number') tuer.__tbBlasen += 1
}

/**
 * What makes a bubble draw again (1.155.0, review D4) -- and what does not.
 *
 * The chat refetches every fifteen seconds and merges the page into the
 * list; TanStack Query keeps the reference of an unchanged message
 * (structural sharing), so `message` is the same object as long as nothing
 * about it changed. The callbacks come from `useStableCallbacks()` in the
 * page and keep their identity. What is left is `auswahl`, an object the
 * page builds per row and per drawing: compared by what it says, not by
 * its identity. Measured in `blasentakt.mjs`: a quiet tick drew 300 of 300
 * rows before, none after.
 */
function gleicheBlase(a: Props, b: Props): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof Props>
  for (const key of keys) {
    if (key === 'auswahl') continue
    if (a[key] !== b[key]) {
      // The second door for the test bench: which prop made the row draw.
      const grund = (globalThis as { __tbBlasenGrund?: Record<string, number> }).__tbBlasenGrund
      if (grund !== undefined) grund[key] = (grund[key] ?? 0) + 1
      return false
    }
  }
  return (
    (a.auswahl === undefined) === (b.auswahl === undefined)
    && a.auswahl?.gewaehlt === b.auswahl?.gewaehlt
    && a.auswahl?.waehle === b.auswahl?.waehle
  )
}

/** Eine Sprechblase. Eigene Nachrichten rechts, fremde links. */
export const Bubble = memo(BubbleInner, gleicheBlase)

function BubbleInner({
  message,
  tripId,
  withUser,
  showAuthor,
  canReact,
  reiseVersatz = null,
  directLabel,
  markiereBroadcast = false,
  onReply,
  onJump,
  onAufDieKarte,
  auswahl,
  onLoeschmodus,
  onVerschiebemodus,
  onPublishToAll,
  onSaveMedia,
  onMenueAuf,
  ziel,
  wirdVerschoben = false,
  entwurf = false,
  onEinfuegen,
  onVeroeffentlichen,
  onTagestrenner,
  onWetter,
  onAbstimmung,
  onTag,
  onGif,
}: Props) {
  zaehleZeichnung()
  const grenzen = useTextgrenzen()
  /** Das zitierte Foto in Großansicht -- nur wenn der Server eines mitschickt. */
  const [zitatOffen, setZitatOffen] = useState(false)
  const zitatBild = message.reply_to?.media_full ?? null
  const edit = useEditMessage(tripId, withUser, entwurf)
  const react = useToggleReaction(tripId, withUser, entwurf)

  // Reaktionen auf die Nachricht selbst; die an den Fotos zeigt die Galerie.
  const own = message.reactions.filter((reaction) => reaction.media_id === 0)
  /**
   * Was in die Galerie geht: Fotos **und Videos** (#86, 1.56.0).
   *
   * Die Trennung stand hier, am Punkt und am Spot dreimal gleich; ob ein
   * Video groß spielt oder als Kachel steht, entscheidet jetzt die Galerie
   * selbst. Sprachnachrichten bleiben draußen, sie sind die Nachricht und
   * kein Anhang.
   */
  const bilder = message.media.filter((item) => item.kind !== 'audio')
  const t = useTexte()
  // **The bubble listens to the clock itself** (1.156.0). Since it is
  // `memo` (1.155.0) a zone change that redraws `ThreadPage` no longer
  // reaches it: the props are the same, and the time under the bubble
  // stayed in the old zone (`zweiuhren.mjs`, red in the full run). A
  // context passes `memo`, so only a zone change redraws every bubble.
  useUhrwechsel()
  const [draft, setDraft] = useState<string | null>(null)
  /** Wo das Menü aufgehen soll — `null` heißt: es ist zu. */
  const [menue, setMenue] = useState<Menueort | null>(null)
  const [werReagierte, setWerReagierte] = useState(false)

  // Wo überhaupt geantwortet werden kann: an einer fremden, nicht gelöschten
  // Sprechblase in einer Reise, die noch läuft -- und nicht, während gerade
  // etwas geändert wird.
  const antwortbar =
    onReply !== undefined && canReact && !message.own && !message.deleted && draft === null

  function save(wortlaut: string, weg: number[]) {
    const text = wortlaut.trim()
    // Unchanged: nothing to send. An **empty** text is a change since 1.88.0
    // -- a photo may lose its caption; a post of nothing the sheet does not
    // offer to save, and the server refuses it as well.
    if (text === message.body && weg.length === 0) {
      setDraft(null)
      return
    }
    edit.mutate(
      {
        id: message.id,
        body: text,
        // What the post keeps, recordings included: the sheet shows only
        // pictures, and what it does not show it does not take away.
        ...(weg.length > 0 ? { media_ids: message.media.map((m) => m.id).filter((id) => !weg.includes(id)) } : {}),
      },
      { onSuccess: () => setDraft(null) },
    )
  }

  /**
   * **Gibt es überhaupt etwas zu lesen?**
   *
   * Aus dem Betrieb: ein Foto ohne Text saß in einer Sprechblase, und die war
   * nichts als ein farbiger Rahmen um ein Bild. Wo nichts steht, steht jetzt
   * auch keine Blase — das Bild spricht für sich.
   *
   * Der Zitatbalken zählt **nicht** dazu: er gehört zu dem, worauf geantwortet
   * wird, und steht dann eben über dem Anhang.
   */
  /**
   * **Kärtchen sprechen für sich — wie ein Foto.**
   *
   * Über ihnen stand einmal ein Satz („Neu auf der Karte"), und darum herum
   * eine Sprechblase. Beides wiederholte, was auf dem Kärtchen ohnehin steht:
   * Titel, Ort, Datum. Aus dem Betrieb gewünscht, und es ist dieselbe
   * Entscheidung wie oben beim Foto — wo nichts zu lesen steht, steht auch
   * keine Blase.
   *
   * Der **gespeicherte** Text bleibt, wie er ist: er trägt die Vorschau in
   * der Chatliste und die Meldung auf dem Sperrbildschirm, und dort gibt es
   * keine Kärtchen.
   */
  const hatKarten = !message.deleted && (message.highlights?.items.length ?? 0) > 0

  /**
   * **Dieselbe Überlegung wie bei den Kärtchen** (#56): der gespeicherte Text
   * („Abstimmung: Wohin morgen?") trägt die Vorschau in der Reiseliste und die
   * Meldung auf dem Sperrbildschirm — dort gibt es keine Abstimmung zum
   * Antippen. In der Sprechblase stünde die Frage damit zweimal.
   */
  const hatAbstimmung = !message.deleted && message.poll !== null
  // The weather card (0.31.0) like the poll: the text ("Wetter in Trondheim:
  // 12 °C, bewölkt") belongs to the preview and the notification, the card
  // to the bubble. `?? null`, because a post from the cache of before 0.31.0
  // does not know the field yet.
  const hatWetter = !message.deleted && (message.weather ?? null) !== null
  // The day card (#79) like the weather: the text is the preview, the card the bubble.
  const hatTag = !message.deleted && (message.day_card ?? null) !== null
  // The day separator (#113): a post of its own since #130, its line is its bubble.
  const trenner = message.day_separator ?? null
  const nurLinie = nurTrenner(message)
  /**
   * **A separator is a post of its own** (#130, 1.115.0): its line is its
   * bubble, and it takes the menu and the circle every post takes, from the
   * same code. One difference, and it stands in the list of what each kind
   * offers, not as a road of its own: a reader gets no menu at a chapter
   * line -- no reaction, no reply (from use, 1.86.4, confirmed).
   */
  const istTrenner = trenner !== null && nurLinie
  // Musik (#102) -- und hier hängt der Text nicht an der Karte, sondern
  // daran, wer ihn geschrieben hat: der Begleittext ist der Grund des
  // Beitrags und bleibt stehen, die Zeile des Servers steht über dem Player
  // nicht noch einmal (`auto_text`).
  const musik = message.deleted ? null : (message.music ?? null)
  const hatMusik = musik !== null
  const musikText = musik !== null && musik.auto_text !== true

  // `draft` stand hier einmal mit drin, weil das Feld in der Blase saß und
  // die Blase deshalb dastehen musste. Seit 1.68.0 liegt es im Blatt darüber.
  const hatText =
    (message.deleted && !nurLinie) ||
    (message.body !== '' && !hatKarten && !hatAbstimmung && !hatWetter && !hatTag && !hatMusik)

  /**
   * Worauf sich das hier bezieht — das Zitat und seine Großansicht.
   *
   * **Steht unter der oberen Zeile und über dem Text**, denn es ist der
   * Anfang des Satzes: erst das Zitat, dann die Antwort darauf. Der Name des
   * Absenders ist mit der Uhrzeit nach oben gewandert und nimmt es nicht mit:
   * ein Zitat ist ein Kasten über die ganze Breite, keine Angabe in einer
   * Zeile.
   */
  const bezug = (
    <>
      {message.reply_to && !message.deleted && (
        /*
         * **Zwei Ziele statt einem** — aus dem Betrieb: „bei der Antwort auf
         * ein Foto soll der Empfänger das Bild auch direkt in der Galerie
         * öffnen können (das thumbnail ist manchmal zu klein um zu erkennen,
         * welches Foto es ist)."
         *
         * Das Bildchen öffnet die Großansicht, der Rest springt weiterhin zur
         * bezogenen Nachricht. Beides in **einen** Knopf zu legen ginge nicht:
         * ein `<button>` in einem `<button>` ist kein gültiges Dokument, und
         * die Sprungausgabe der Tastatur käme durcheinander. Also ein Kasten
         * mit zwei Knöpfen darin.
         */
        <div className="zitat">
          {zitatBild !== null ? (
            <button
              type="button"
              className="zitat__lupe"
              onClick={() => setZitatOffen(true)}
              aria-label={t.chat.fotoGross}
              title={t.chat.fotoGrossKurz}
            >
              <img className="zitat__bild" src={message.reply_to.media_url ?? zitatBild.url} alt="" />
            </button>
          ) : (
            message.reply_to.media_url && (
              <img className="zitat__bild" src={message.reply_to.media_url} alt="" />
            )
          )}
          <button
            type="button"
            className="zitat__sprung"
            onClick={() => onJump?.(message.reply_to!.id)}
            title={t.chat.zumBezug}
          >
            <span className="zitat__text">
              <span className="zitat__autor">{message.reply_to.author}</span>
              <span className="zitat__auszug">
                {message.reply_to.deleted
                  ? t.chat.geloescht
                  : message.reply_to.body !== ''
                    ? <Ausgezeichnet text={message.reply_to.body} />
                    : medienwort(message.reply_to.media_kind)}
              </span>
            </span>
          </button>
        </div>
      )}

      {/* **Dieselbe Großansicht wie überall** -- mit Zoom, Sichern und allem.
          Nur zu reagieren gibt es hier nichts: das Foto gehört einer anderen
          Nachricht, und wer darauf antworten will, springt hin. */}
      {zitatOffen && zitatBild !== null && (
        <Lightbox
          media={[zitatBild]}
          start={0}
          reactionsFor={() => []}
          canReact={false}
          onReact={() => undefined}
          onClose={() => setZitatOffen(false)}
        />
      )}
    </>
  )

  /** Wer es geschrieben hat und wie weit es reicht — links in der Kopfzeile. */
  const wer = (
    <span className="bubble__wer">
      {showAuthor && !message.deleted && (
        <span className="bubble__author">{message.author.display_name}</span>
      )}

      {/* Eine Nachricht an einen Einzelnen muss als solche dastehen. Ohne
          diese Zeile hält der Mitleser sie für einen Travel Broadcast -- und
          antwortet, als hätten alle mitgelesen.

          **Eine Person statt eines Schlosses.** Aus dem Betrieb: „das deutet
          aber auf Verschlüsselung hin." Es deutet auf eine, die es nicht
          gibt -- und mit #23 gäbe es sie und das Zeichen meinte trotzdem
          etwas anderes. Eine Person sagt, was gemeint ist: **einer** sieht
          es.

          **Nur das Sinnbild, kein Wort.** Aus dem Betrieb: „ohne Wort, nur
          Symbol" -- so, wie das Schloss vorher dastand. Ein Wort an jeder
          Blase ist eine Zeile, die bei jeder Nachricht dasselbe sagt.

          **Der Satz verschwindet trotzdem nicht**, er wird unsichtbar: wer
          vorlesen lässt, hört weiterhin, wie weit die Nachricht reicht. Er
          steht als Text im Dokument und nicht als `aria-label` an einem
          Element ohne Rolle; ein `title` allein ist auf dem Telefon ohnehin
          unsichtbar. Das Sinnbild daneben trägt `aria-hidden`, sonst käme
          sein Name vor den Satz. */}
      {message.kind === 'direct' && !message.deleted && (
        <span className="bubble__reichweite" title={directLabel ?? t.chat.nurAnDich}>
          <PersonIcon size={15} aria-hidden="true" />
          <span className="visually-hidden">{directLabel ?? t.chat.nurAnDich}</span>
        </span>
      )}

      {/* **Und was an alle ging, sagt das auch** -- wenn die Reise es so
          hält. Aus dem Betrieb: „beide markieren, aber die Option auch im
          Broadcast konfigurierbar machen für den Admin - auf Reise-Ebene".
          Ein Bericht, in dem ohnehin fast alles an alle geht, trüge sonst an
          jeder Blase dieselbe Marke; einer mit vielen Einzelnachrichten
          gewinnt durch sie. Deshalb je Reise und nicht ein für alle Mal. */}
      {markiereBroadcast && message.kind === 'broadcast' && !message.deleted && (
        <span className="bubble__reichweite" title={t.chat.anAlleEmpfaenger}>
          <GruppeIcon size={15} aria-hidden="true" />
          <span className="visually-hidden">{t.chat.anAlle}</span>
        </span>
      )}
    </span>
  )

  /**
   * Zeit, Uhr des Absenders und Haken.
   *
   * Sie hängen an der **Nachricht** und nicht an der Blase. Deshalb stehen sie
   * hier einmal und werden dorthin gesetzt, wo die Nachricht anfängt: in die
   * Blase, wenn es eine gibt — sonst über den Anhang.
   */
  const meta = (
    <span className={hatText ? 'bubble__meta' : 'bubble__meta bubble__meta--frei'}>
      {/* The written time of a published draft (#85), not the publishing:
          "10:14 Ankunft am Pass" stays 10:14. */}
      {/* With its date in front when it stands under another day (#130). */}
      {bubbleTimeUnder(message.written_at ?? message.created_at, dayMoment(message))}
      {/*
        **Die zweite Uhrzeit, und nur wenn sie abweicht** (#71). Sitzt der
        Autor in Norwegen und die Familie zu Hause, stand hier „14:20" und
        niemand wusste, dass es beim Absender 15:20 war. Fehlt die Angabe (alte
        Nachricht), bleibt es bei einer -- geraten wird nicht.
      */}
      {absenderZeit(message.written_at ?? message.created_at, message.tz_offset_min)}
      {message.edited_at && !message.deleted && (
        <span title={t.chat.nachtraeglichGeaendert}>{t.chat.bearbeitet}</span>
      )}
      <Haken read={message.read} />
    </span>
  )

  /**
   * Die obere Zeile: links wer, rechts wann.
   *
   * **Aus dem Betrieb:** „Zeit und Statusinformationen in die obere Zeile."
   *
   * Vorher schwamm die Uhrzeit als `float: right` im Text und landete
   * irgendwo in der ersten oder zweiten Zeile — der Bau, den WhatsApp für
   * seine *untere* rechte Ecke benutzt. Mit der zweiten Uhrzeit (#71) wurde
   * daraus „16:29 · 12:59 vor Ort ✓✓": ein Block, der die erste Textzeile auf
   * ein paar Wörter zusammendrückt und bei jeder Blase woanders sitzt.
   *
   * Jetzt hat er eine eigene Zeile, und der Name des Absenders steht auf
   * derselben — die Zeile kostet damit keine Höhe, wo ohnehin schon ein Name
   * stand. Wo keiner steht (eigene Nachrichten), bleibt links Platz und die
   * Angabe sitzt rechts, immer an derselben Stelle.
   */
  const kopfzeile = (
    <span className="bubble__kopfzeile">
      {wer}
      {meta}
    </span>
  )

  /**
   * Was man mit dieser Nachricht tun kann — im Menü statt in der Blase (#8).
   *
   * **Die Bedingungen sind dieselben wie vorher**, nur der Ort ist ein
   * anderer: hier stand vier Mal ein `<button>` mit genau diesen `&&`
   * davor. Zeit, „bearbeitet" und Haken sind absichtlich **nicht**
   * mitgegangen — sie sagen etwas über die Nachricht und tun nichts;
   * ausdrücklich so gewünscht („hier würde ich beim WhatsApp-Stil bleiben").
   */
  /**
   * Das eine Medium, wenn es genau eines ist.
   *
   * **Damit „Antworten" im Menü so genau bleibt wie der Knopf, der dafür
   * weggefallen ist.** Am Video stand „Auf dieses Video antworten" und gab
   * dessen Kennung mit; ohne sie zeigte das Zitat über der Eingabezeile kein
   * Standbild und darunter das Wort „Foto". Aus dem Betrieb: „außerdem kann
   * dann bei Videos der Knopf ‚auf dieses Video antworten' entfallen."
   *
   * **Abgeleitet und nicht am Griff festgemacht:** wo genau der Finger lag,
   * müsste jedes Medium einzeln melden. Bei genau einem Anhang ist die Frage
   * ohnehin entschieden -- und ein Video liegt praktisch immer allein in
   * seiner Nachricht. Bei mehreren Fotos führt der Weg weiter über die
   * Großansicht, die die Kennung kennt.
   */
  const einzelnesMedium = message.media.length === 1 ? message.media[0] : undefined

  const eintraege: Menueeintrag[] = []
  if (!message.deleted && draft === null && !(istTrenner && !message.own)) {
    // Auf eine eigene Nachricht zu antworten ergibt keinen Sinn.
    if (antwortbar) {
      eintraege.push({
        name: t.chat.menueAntworten,
        symbol: <ReplyIcon size={17} />,
        tun: () => onReply(message, einzelnesMedium?.id),
      })
    }
    if (hatText) {
      eintraege.push({
        name: t.chat.menueKopieren,
        symbol: <KopierenIcon size={17} />,
        // `writeText` kann fehlschlagen -- ohne sicheren Kontext, ohne
        // Erlaubnis. Dann passiert nichts, und das ist hier in Ordnung: es
        // ist kein Schreibzugriff auf die Reise, sondern eine Bequemlichkeit
        // des Geräts. Der Grund geht trotzdem in den Mitschrieb
        // (Codereview C10) -- „Kopieren tut nichts" ist sonst nicht
        // nachzuvollziehen.
        tun: () =>
          void navigator.clipboard
            ?.writeText(message.body)
            .catch((fehler) => notiere(`Kopieren nicht möglich: ${String(fehler)}`)),
      })
    }
    /*
     * **Saving from the menu** (1.159.0). A single video in the chat is a
     * player, and a tap plays it; there is no large view with the button
     * photos have. Decided from use: in the menu -- and then "auch wenn
     * mehrere Bilder / Videos angezeigt werden (4er-Raster)" all of them at
     * once. A single photo opens its large view on a tap, with its button;
     * a voice message is not a picture.
     */
    const sicherbar = message.media.filter((m) => m.kind === 'image' || m.kind === 'video')
    if (onSaveMedia && (sicherbar.length > 1 || sicherbar[0]?.kind === 'video')) {
      const fotos = sicherbar.filter((m) => m.kind === 'image').length
      eintraege.push({
        name: sicherbar.length === 1 ? t.chat.videoSichern : t.chat.medienSichern(fotos, sicherbar.length - fotos),
        schluessel: sicherbar.length === 1 ? 'video-sichern' : 'medien-sichern',
        symbol: <SichernIcon size={17} />,
        tun: () => onSaveMedia(sicherbar),
      })
    }
    // Who reacted (#110) -- to the post and to each of its photos, in one
    // sheet. Offered wherever the server sent names beyond one's own; that
    // is the author, and whoever wrote this message.
    if (zeigtFremdeNamen(message.reactions)) {
      eintraege.push({
        name: t.chat.reaktionenMenue,
        schluessel: 'reaktionen',
        symbol: <SmileyIcon size={17} />,
        tun: () => setWerReagierte(true),
      })
    }
    if (message.own) {
      /*
       * **What a long press offers depends on the kind of post** (1.86.0).
       * A card (poll, weather, day card, music) carries a line the server
       * wrote for the preview and the push; editing that text as if it were
       * a post overwrote the line and, from use, "deleted the music". The
       * rule stands once, here: a post of words may become a point and may
       * be edited; a card is changed through its own sheet (`karte`).
       *
       * **A day separator is a kind like the weather card** (#130): its
       * change is its sheet ("Tagestrenner ändern"), it is never put on the
       * map and never takes text.
       */
      const karte = hatAbstimmung || hatWetter || hatTag || hatMusik
      // A post of nothing but a GIF (1.86.0): its change mode is the chooser.
      const nurGif = !message.deleted && message.body === '' && message.media.length > 0
        && message.media.every((m) => m.mime === 'image/gif')
      // A voice message keeps no words (1.116.0, `voiceAlone()`): no
      // "Ändern", and no point on the map from a recording.
      const istTon = message.media.some((m) => m.kind === 'audio')
      const nurWorte = !hatKarten && !karte && !nurGif && !istTrenner && !istTon
      // Aus dem Absatz einen Punkt auf der Karte machen. Text, Zeitpunkt und
      // die Bilder gehen mit hinüber; wo es war, entscheidet die Karte -- aus
      // den Bildern, wenn sie es wissen.
      if (onAufDieKarte && nurWorte) {
        eintraege.push({
          name: t.chat.menueAufDieKarte,
          schluessel: 'karte',
          symbol: <PinIcon size={17} />,
          tun: () => onAufDieKarte(message),
        })
      }
      // **Diesen Text schreibt die Karte, nicht der Mensch.** Er steht in
      // der Chatliste und auf dem Sperrbildschirm, in der Sprechblase gar
      // nicht mehr — ein selbst geschriebener Satz wäre hier also
      // unsichtbar. Deshalb kein Ändern; der Server lehnt es zusätzlich ab.
      //
      // **Music keeps it** (1.86.0): its own line is the human's companion
      // text, and the player stays -- the answer to the edit carries the
      // card (`MessagesController::anhaengeFuer()`).
      if (nurWorte || hatMusik) {
        eintraege.push({
          name: t.chat.menueAendern,
          symbol: <PencilIcon size={17} />,
          schluessel: 'aendern',
          tun: () => setDraft(message.body),
        })
      }
      // The weather card at a chosen place and hour (1.86.0): its data
      // anew, as when it was made. The card from the device has no entry.
      if (onWetter && hatWetter && message.weather?.located === false) {
        eintraege.push({
          name: t.chat.menueWetterAendern,
          symbol: <SonneIcon size={17} />,
          schluessel: 'wetter',
          tun: () => onWetter(message),
        })
      }
      // The poll: question and words anew, the answers stay (1.86.0).
      if (onAbstimmung && hatAbstimmung) {
        eintraege.push({
          name: t.chat.menueAbstimmungAendern,
          symbol: <PollIcon size={17} />,
          schluessel: 'abstimmung',
          tun: () => onAbstimmung(message),
        })
      }
      // The day card: its day and part anew, the map drawn again (1.86.0).
      if (onTag && hatTag) {
        eintraege.push({
          name: t.chat.menueTagAendern,
          symbol: <BergIcon size={17} />,
          schluessel: 'tag',
          tun: () => onTag(message),
        })
      }
      if (onGif && nurGif) {
        eintraege.push({
          name: t.chat.menueGifAendern,
          symbol: <GifIcon size={17} />,
          schluessel: 'gif',
          tun: () => onGif(message),
        })
      }
      // The separator's sheet (#113): its day, count and note, like a
      // weather card's sheet for the card.
      if (istTrenner && onTagestrenner) {
        eintraege.push({
          name: t.chat.tagestrenner.menueAendern,
          schluessel: 'tagestrenner',
          symbol: <KalenderIcon size={17} />,
          tun: () => onTagestrenner(message),
        })
      }
      /*
       * **Before or after this post** (#130, 1.115.0): the author's posts to
       * everyone, in the chat and in the draft alike -- the separator too.
       * The full plus menu goes with it: the marker stands above the line,
       * and whatever is sent from there goes to that place.
       */
      if (onEinfuegen && message.kind === 'broadcast') {
        eintraege.push({
          name: t.chat.einfuegen.menueDavor,
          schluessel: 'einfuegen-davor',
          symbol: <PlusIcon size={17} />,
          tun: () => onEinfuegen(message, 'before'),
        })
        eintraege.push({
          name: t.chat.einfuegen.menueDanach,
          schluessel: 'einfuegen-danach',
          symbol: <PlusIcon size={17} />,
          tun: () => onEinfuegen(message, 'after'),
        })
      }
      /*
       * **Publishing a draft** (#85): "up to this one", chosen by the long
       * press, is exactly what the menu is for. Moving a draft goes through
       * the selection below since 1.135.0, as in the chat; "Nach oben" and
       * "Nach unten" moved one step per tap.
       */
      if (entwurf) {
        if (onVeroeffentlichen) {
          eintraege.push({
            name: t.chat.entwurf.menueVeroeffentlichen,
            schluessel: 'veroeffentlichen',
            symbol: <SendIcon size={17} />,
            tun: () => onVeroeffentlichen(message),
          })
        }
      }
      /*
       * **Löschen öffnet die Auswahl, statt sofort zu fragen** (#30).
       *
       * Aus dem Betrieb, nach dem Vorbild von WhatsApp und iMessage: „nach
       * Auswahl von Löschen im Menü öffnet sich unten eine Leiste mit einem
       * Papierkorb-Symbol; die Nachricht, aus der heraus das Löschen
       * aufgerufen wurde, ist bereits markiert."
       *
       * Für eine einzelne Nachricht ist das ein Griff mehr als vorher. Der
       * Gewinn steht daneben: zehn Nachrichten kosten jetzt auch nur einen.
       */
      /*
       * **Moving and deleting are two entries** (1.117.3). 1.117.1 had one,
       * "Löschen/Verschieben", not red. From use: "man möchte Verschieben
       * und wählt aus Versehen Löschen" -- so each opens its own selection,
       * and deleting is red again.
       */
      if (onVerschiebemodus && message.kind === 'broadcast') {
        eintraege.push({
          name: t.chat.verschieben.menue,
          schluessel: 'verschieben',
          symbol: <MoveIcon size={17} />,
          tun: () => onVerschiebemodus(message.id),
        })
      }
      // A private message to everyone (1.157.0): moved, not copied.
      if (onPublishToAll && message.kind === 'direct' && !message.deleted) {
        eintraege.push({
          name: t.chat.publishToAll.menue,
          schluessel: 'publish-to-all',
          symbol: <GruppeIcon size={17} />,
          tun: () => onPublishToAll(message),
        })
      }
      eintraege.push({
        name: t.chat.menueLoeschen,
        schluessel: 'loeschen',
        symbol: <TrashIcon size={17} />,
        gefahr: true,
        tun: () => onLoeschmodus?.(message.id),
      })
    }
  }

  const griffe = useLangdruck((ort) => {
    setMenue(ort)
    onMenueAuf?.()
  })

  /**
   * Die Emoji-Reihe über dem Menü — dieselbe Liste wie im Server.
   *
   * **Ohne Netz abgeschaltet, nicht stumm.** Ein Emoji ist ein Schreibzugriff
   * wie jeder andere; das war schon am alten Knopf so und bleibt es hier.
   */
  const online = useOnline()
  // A post under a chapter line takes reactions like any other (1.86.1):
  // the line is the separator's, the post is the post.
  const reaktionen = canReact && !message.deleted && !istTrenner
    ? {
        emojis: EMOJI,
        eigene: own.filter((reaktion) => reaktion.mine).map((reaktion) => reaktion.emoji),
        gesperrt: !online || react.isPending,
        hinweis: online ? undefined : t.allgemein.ohneNetz,
        waehle: (emoji: string) => react.mutate({ messageId: message.id, emoji }),
      }
    : undefined

  /**
   * Fotos und Videos — **unter** der Blase, nicht darin.
   *
   * Aus dem Betrieb gewünscht, und es ist auch das treffendere Bild: eine
   * Sprechblase ist für Gesagtes. Ein Bild ist kein Satz, es braucht keinen
   * Rahmen, und in der Blase drängte es den Text an den Rand.
   *
   * Videos stehen für sich, Bilder in der Galerie. Ein Video in ein Raster aus
   * Quadraten zu stecken hieße, einen Player auf ein Vorschaubild zu
   * schrumpfen -- und die Großansicht der Galerie ist fürs Blättern durch
   * Fotos gebaut, nicht fürs Abspielen.
   */
  const anhang = message.deleted ? null : (
    <>
      {/* **Sprachnachrichten zuerst** — sie sind die Nachricht, nicht ihr
          Anhang. Ein eingebauter Player und kein eigener: das Bedienelement
          des Geräts kennt jeder, und ein nachgebauter kann weniger. */}
      {message.media
        .filter((item) => item.kind === 'audio')
        .map((item) => (
          <Tonspur key={item.id} url={item.url} dauerMs={item.duration_ms} />
        ))}

      {bilder.length > 0 && (
        <Gallery
          media={bilder}
          reactions={message.reactions}
          canReact={canReact}
          onReact={(emoji, mediaId) =>
            react.mutate({ messageId: message.id, emoji, mediaId })
          }
          onReply={antwortbar ? (mediaId) => onReply!(message, mediaId) : undefined}
        />
      )}
    </>
  )
  const hatAnhang = !message.deleted && message.media.length > 0

  const reaktionsliste = !message.deleted && own.length > 0 && (
    <>
      <Reactions
        reactions={own}
        busy={react.isPending}
        // Der Knopf zum Reagieren liegt jetzt im Menü beim langen Drücken --
        // hier steht nur noch, was vergeben ist.
        nurAnzeigen
        onPick={(emoji) => canReact && react.mutate({ messageId: message.id, emoji })}
      />
      {/* Stillschweigend nichts tun gibt es nicht -- auch ein Emoji sagt,
          wenn es nicht ankam (Reise abgeschlossen, Nachricht geloescht). */}
      {react.isError && (
        <span className="feld__hinweis" role="alert">
          {fehlertext(react.error)}
        </span>
      )}
    </>
  )

  /**
   * **One circle, the post's** (#130). A separator is a post: its circle
   * stands before its line as before every other bubble. Until 1.114 a line
   * over a post had a circle of its own.
   */
  const beitragWahl = auswahl
  const imAuswahlmodus = auswahl !== undefined

  return (
    <div
      className={[
        'bubble-row',
        message.own ? 'bubble-row--own' : '',
        // Nur wo ein Kreis steht, wird auch Platz dafür gemacht.
        imAuswahlmodus ? 'bubble-row--waehlbar' : '',
        beitragWahl?.gewaehlt === true || wirdVerschoben ? 'bubble-row--gewaehlt' : '',
        ziel !== undefined ? 'bubble-row--zielwahl' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      // Das Ziel für den Sprung aus einem Zitat.
      id={`msg-${message.id}`}
      /**
       * Der Griff zum Menü liegt an der ganzen Zeile und nicht an der Blase:
       * eine Nachricht aus einem einzigen Foto hat gar keine Blase.
       *
       * **Nur, wenn es etwas zu tun gibt.** Sonst wäre jede fremde Nachricht
       * in einer abgeschlossenen Reise eine Station in der Tab-Reihenfolge,
       * die auf ein leeres Menü führt.
       */
      {...(ziel !== undefined
        ? { onClick: () => ziel(message), 'data-zielwahl': '' }
        : beitragWahl !== undefined
        ? { onClick: () => beitragWahl.waehle(message.id) }
        : imAuswahlmodus
          ? {}
          : eintraege.length > 0
          ? griffe
          : {})}
      tabIndex={ziel !== undefined || eintraege.length > 0 ? 0 : undefined}
      role={ziel !== undefined || eintraege.length > 0 ? 'button' : undefined}
      aria-haspopup={ziel === undefined && eintraege.length > 0 ? 'menu' : undefined}
      aria-label={ziel !== undefined ? t.chat.verschieben.zielName : eintraege.length > 0 ? t.chat.blaseName : undefined}
    >
      {/*
        * **Der Kreis steht vor der Nachricht** (#30), wie in WhatsApp.
        *
        * Er ist ein echter Knopf und keine Verzierung: 44 × 44, mit
        * `aria-pressed`, damit eine Vorlesehilfe den Zustand nennt. Ein Tipp
        * auf die Blase selbst schaltet ihn ebenfalls um -- den Weg nimmt der
        * Daumen, der Kreis ist die Auskunft, dass es ihn gibt.
        */}
      {/* A separator's line is its bubble (#130): the long press, the menu
          and the circle are the row's, as for every kind. */}
      {istTrenner && trenner !== null && <Tagestrenner trenner={trenner} />}
      {beitragWahl !== undefined && (
        <button
          type="button"
          className={beitragWahl.gewaehlt ? 'wahlkreis wahlkreis--an' : 'wahlkreis'}
          aria-pressed={beitragWahl.gewaehlt}
          aria-label={beitragWahl.gewaehlt ? t.chat.auswahlAufheben : t.chat.zumLoeschenWaehlen}
          onClick={(event) => {
            event.stopPropagation()
            beitragWahl.waehle(message.id)
          }}
        >
          <span className="wahlkreis__punkt" />
        </button>
      )}
      {werReagierte && (
        <Reaktionsliste
          gruppen={[
            { titel: t.chat.reaktionenZumBeitrag, reactions: own },
            ...message.media.map((medium, index) => ({
              titel: t.chat.reaktionenAnhang(medium.kind, index + 1),
              reactions: message.reactions.filter((r) => r.media_id === medium.id),
            })),
          ]}
          onZu={() => setWerReagierte(false)}
        />
      )}

      {menue !== null && (
        <Blasenmenue
          eintraege={eintraege}
          reaktionen={reaktionen}
          ort={menue}
          onSchliessen={() => setMenue(null)}
        />
      )}
      {draft !== null && (
        <Aenderungsblatt
          wortlaut={draft}
          bilder={bilder}
          bleibtSonst={message.media.some((m) => m.kind === 'audio') || trenner !== null || hatMusik}
          grenze={grenzen.message}
          laeuft={edit.isPending}
          fehler={edit.isError ? fehlertext(edit.error) : null}
          onSichern={save}
          onZu={() => setDraft(null)}
        />
      )}
      {hatText && (
        <div className={message.deleted ? 'bubble bubble--deleted' : 'bubble'}>
          {kopfzeile}
          {bezug}

          {message.deleted ? (
            t.chat.geloescht
          ) : (
            /**
             * **Freigaben stehen hier nicht mehr.**
             *
             * Der gespeicherte Wortlaut („Neu auf der Karte: Nidarosdom")
             * trägt weiterhin die Vorschau in der Reiseliste und die Meldung
             * auf dem Sperrbildschirm — dort gibt es keine Kärtchen. In der
             * Sprechblase stünde der Titel damit zweimal, einmal im Satz und
             * einmal auf dem Kärtchen; deshalb kommt eine Nachricht mit
             * Kärtchen gar nicht bis hierher (siehe `hatText`).
             *
             * **Beiträge von vor der Kärtchen-Änderung tragen keine** und
             * zeigen unverändert ihren Text — sonst stünde dort nichts.
             */
            /* **Der Deckel sitzt aussen, die Auszeichnung innen.** Der
               Deckel misst am rohen Wortlaut und zeichnet nichts selbst --
               sonst gaebe es zwei Wege, einen Text darzustellen. */
            <Langtext text={message.body}>
              <Ausgezeichnet text={message.body} links={message.links_from ?? 'reader'} />
            </Langtext>
          )}

          {!hatAnhang && !hatKarten && reaktionsliste}
        </div>
      )}

      {/* **Die Kärtchen stehen für sich, ohne Blase** — derselbe Bau wie beim
          Anhang darunter, und aus demselben Grund. Kopf und Meta wandern
          hierher, wenn es keine Blase gibt: sonst wüsste niemand, von wem der
          Beitrag ist und wann er kam. */}
      {hatKarten && (
        <div className="hlblock">
          {!hatText && kopfzeile}
          {!hatText && bezug}
          <Highlightkarten message={message} />
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {/* **Die Abstimmung steht für sich, ohne Blase** — derselbe Bau wie die
          Kärtchen darüber und aus demselben Grund: sie ist breiter als eine
          Sprechblase und braucht Trefferflächen. */}
      {hatAbstimmung && (
        <div className="hlblock">
          {!hatText && kopfzeile}
          {!hatText && bezug}
          <Abstimmungskarte poll={message.poll!} tripId={tripId} />
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {hatWetter && (
        <div className="hlblock hlblock--breit">
          {!hatText && kopfzeile}
          {!hatText && bezug}
          <Wetterkarte karte={message.weather!} />
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {hatTag && (
        <div className="hlblock hlblock--breit">
          {!hatText && kopfzeile}
          {!hatText && bezug}
          {/* Start and end in the zone course at their own moments
              (1.87.1), not in the offset of the post: that is the clock of
              the device that posted it, perhaps days later at home. */}
          {/* Only the map (#132): the picture or film below is the post. */}
          {showsNumbers(message.day_card!) && <Tageskarte karte={message.day_card!} versatz={reiseVersatz} />}
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {/* **The text is the heading, above the card** (1.71.0). From the
          field: "der Text soll als Überschrift vor der Karte stehen." Until
          then it stood as a bubble of its own above the card; now the music
          post has no bubble at all (see `hatText`), and head, quote and text
          stand in this block like with the other cards. The server's own line
          (`auto_text`) stays out, as before -- it would say the title twice. */}
      {hatMusik && (
        <div className="hlblock hlblock--breit">
          {kopfzeile}
          {bezug}
          {musikText && message.body !== '' && (
            /* `role="heading"` on a `div` and not an `h3`: `Langtext` puts a
               box and its "Mehr" button inside, and neither belongs in an
               `h3`. */
            <div className="musikkarte__ueberschrift" role="heading" aria-level={3} data-musik-ueberschrift="">
              <Langtext text={message.body}>
                <Ausgezeichnet text={message.body} links={message.links_from ?? 'reader'} />
              </Langtext>
            </div>
          )}
          <Musikkarte karte={musik} />
          {!hatAnhang && reaktionsliste}
        </div>
      )}

      {hatAnhang && (
        <div className="anhang">
          {/* Ohne Blase steht der Kopf hier -- sonst wüsste bei einem Foto
              ohne Text niemand, worauf es sich bezieht. Not when the day
              card above carries it already: its map is the photo, and a
              second head with the same time read as a second post. */}
          {!hatText && !hatTag && kopfzeile}
          {!hatText && !hatTag && bezug}
          {anhang}
          {reaktionsliste}
        </div>
      )}
    </div>
  )
}

/**
 * Der Haken an einer eigenen Nachricht.
 *
 * **Ein Haken** heißt „liegt bereit", **zwei** heißen „gelesen" — dieselbe
 * Bedeutung wie in der Chatliste, und aus demselben Grund keine dritte Stufe:
 * ein „zugestellt" im Sinne eines Messengers kann es hier nicht geben. Ohne
 * dauerhafte Verbindung weiß der Server nicht, ob ein Gerät die Nachricht
 * schon geholt hat.
 *
 * Geht die Nachricht an mehrere — ein Rundbeitrag an alle Empfänger —, steht
 * die Zahl daneben: *2/5*. Erst wenn alle gelesen haben, wird sie grün. Ohne
 * die Zahl wäre der Haken eine Behauptung über eine Gruppe, in der er für die
 * meisten nicht stimmt.
 *
 * Ist `read` null, steht hier nichts. Wo nichts zugestellt wird, ist jeder
 * Haken eine Behauptung ins Blaue.
 */
function Haken({ read }: { read: { by: number; of: number } | null }) {
  const t = useTexte()
  if (read === null) return null

  const alle = read.by >= read.of
  const text =
    read.of === 1
      ? alle
        ? t.chat.gelesen
        : t.chat.liegtBereit
      : t.chat.vonGelesen(read.by, read.of)

  return (
    <span
      className={alle ? 'bubble__haken bubble__haken--gelesen' : 'bubble__haken'}
      title={text}
    >
      {read.by > 0 ? <DoubleCheckIcon size={13} /> : <CheckIcon size={13} />}
      {read.of > 1 && (
        <span className="bubble__haken-zahl">
          {read.by}/{read.of}
        </span>
      )}
      <span className="visually-hidden">{text}</span>
    </span>
  )
}
